import * as Sentry from '@sentry/react';
import { createContext, useContext } from 'react';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { fetchAgent, getAgentMetadataHelpers } from '@/entities/agent';
import { StreamMessageEvents } from '@/entities/ai';
import { createChatId, getChatSuggestions } from '@/features/chats/api/chats.api';
import { getChatStores } from '@/features/chats/lib/chat-store-bindings';
import { queryClient } from '@/lib/config';
import { logger } from '@/lib/utils/logger';
import { track } from '@/shared/analytics';
import { i18n } from '@/shared/i18n';
import {
  RECENT_SESSIONS_QUERY_KEY_ROOT,
  SESSIONS_QUERY_KEY_ROOT,
} from '@/shared/constants/query-keys.constants';
import {
  ChatActions,
  ChatState,
  ProjectContextSelection,
  SendMessageInput,
} from './chat.store.types';
import { areSuggestionsHidden } from '../model/suggestionsVisibility/suggestionsVisibility.store';

/** Sets the agent's initial widget data and opens its panel when the agent config asks to. */
function applyInitialWidget(agentId: string) {
  const { autoOpen, layoutMode, data } = getAgentMetadataHelpers().getInitialWidgetState(agentId);

  if (data) {
    const widgetStore = getChatStores().widgetStore.useWidgetStore.getState();
    const currentWidget = widgetStore.widget;

    // Only set initial widget data if there's no existing widget for this agent
    // This preserves state across navigation
    if (!currentWidget || currentWidget.key !== data.key) {
      widgetStore.setWidget(data);
    }
  }

  if (autoOpen) {
    const uiState = getChatStores().uiStore.useUIStore.getState();
    if (uiState.sidePanelType !== 'widget') {
      uiState.openSidePanel('widget', layoutMode);
    }
  }
}

export const initialState: ChatState = {
  messages: [],
  chatInput: {
    value: '',
    files: [],
    options: undefined,
  },
  events: new Map(),
  suggestions: [],
  isSuggestionsLoading: false,
  currentAgentId: '',
  currentAgentState: {},
  agent: null,
  isAgentMissing: false,
  chatId: null,
  error: null,
  uiStatus: null,
  aiStatus: null,
  busyAgentId: null,
  isConnected: false,
  _pendingWidgets: {},
};

const isAgentUnavailable = (state: ChatState) =>
  state.isAgentMissing || state.agent?.isDeleted === true;

const createChatStoreHook = (name: string, inDevtools: boolean) =>
  create<ChatState & ChatActions>()(
    devtools(
      (set, get) => {
        // AbortController to cancel in-flight suggestions fetch when agent changes
        let suggestionsAbortController: AbortController | null = null;
        const cancelSuggestionsFetch = () => {
          suggestionsAbortController?.abort();
          suggestionsAbortController = null;
        };

        // Set when a fetch was skipped because the suggestions were hidden, or failed.
        let suggestionsBehind = false;

        // Store the override function for sendMessage
        let sender: ((input: SendMessageInput) => Promise<void>) | null = null;
        let senderTracked = true;

        // The id whose card is on its way. Until it lands, `agent` is still the previous agent's.
        let loadingAgentId: string | null = null;

        // Advanced by every load and reload of a card, so each writes only if no other began after
        // it, including a return to the same agent.
        let cardGeneration = 0;

        // Internal helper: fetch agent and update store (not exposed in public API)
        const loadAgent = async (agentId: string) => {
          if (!agentId) {
            throw new Error('Agent ID is required to load agent');
          }
          // Supersedes any card load or reload still in flight.
          const generation = ++cardGeneration;
          loadingAgentId = agentId;
          try {
            const agent = await fetchAgent(agentId);
            if (generation !== cardGeneration) return;
            set({
              currentAgentId: agentId,
              agent,
              isAgentMissing: agent === null,
            });
          } catch (error) {
            // A newer reload of this agent cancels this load's query, so a superseded failure says
            // nothing about the agent and must not revert the newer card.
            if (generation !== cardGeneration) return;
            throw error;
          } finally {
            if (loadingAgentId === agentId) loadingAgentId = null;
          }
        };

        const reloadCurrentAgent = async () => {
          const agentId = get().currentAgentId;
          if (!agentId) return;
          const generation = ++cardGeneration;
          try {
            // Removed rather than refetched: the language switch invalidates `['agent']` right
            // after this, and a data-less query in flight is joined by that refetch, where one
            // holding data would be cancelled and fetched a second time.
            queryClient.removeQueries({ queryKey: ['agent', agentId] });
            const agent = await fetchAgent(agentId);
            if (generation !== cardGeneration || get().currentAgentId !== agentId) return;
            set({ agent, isAgentMissing: agent === null });
          } catch (error) {
            logger.error('[ChatStore] Failed to reload current agent:', error);
          }
        };

        return {
          ...initialState,
          setChatId: (chatId: string) => set({ chatId }),
          setSender: (override, options) => {
            sender = override;
            senderTracked = options?.analytics ?? true;
          },
          setInput: (setter) =>
            set((state) => ({
              chatInput: typeof setter === 'function' ? setter(state.chatInput) : setter,
            })),
          setInputValue: (setter) =>
            set((state) => ({
              chatInput: {
                ...state.chatInput,
                value: typeof setter === 'function' ? setter(state.chatInput.value) : setter,
              },
            })),
          setInputFiles: (setter) =>
            set((state) => ({
              chatInput: {
                ...state.chatInput,
                files: typeof setter === 'function' ? setter(state.chatInput.files) : setter,
              },
            })),
          setInputOptions: (setter) =>
            set((state) => ({
              chatInput: {
                ...state.chatInput,
                options: typeof setter === 'function' ? setter(state.chatInput.options) : setter,
              },
            })),
          sendMessage: async ({
            prompt,
            inputs,
            agentId,
            files,
            knowledgeBaseIds,
            isHidden,
            projectContext,
            widgetAction,
          }) => {
            if (isAgentUnavailable(get())) {
              logger.warn('Cannot send message: the current agent is unavailable.');
              return;
            }

            const chatIdAtSend = get().chatId;
            if (chatIdAtSend && senderTracked) {
              track('agent_message_sent', {
                agent_id: agentId,
                session_id: chatIdAtSend,
                message_length: prompt.length,
                has_attachment:
                  (files?.length ?? 0) > 0 ||
                  (projectContext?.briefs.length ?? 0) > 0 ||
                  (projectContext?.artifactIds.length ?? 0) > 0,
              });

              const videoCount = files?.filter((f) => f.mimeType.startsWith('video/')).length ?? 0;
              if (videoCount > 0) {
                track('chat_video_attached', {
                  agent_id: agentId,
                  session_id: chatIdAtSend,
                  video_count: videoCount,
                });
              }
            }

            if (sender) {
              // Switch from fullscreen to split when sending a message
              const uiState = getChatStores().uiStore.useUIStore.getState();
              if (
                uiState.sidePanelType === 'widget' &&
                uiState.sidePanelLayoutMode === 'fullscreen'
              ) {
                uiState.setSidePanelLayoutMode('split');
              }

              return sender({
                prompt,
                inputs,
                agentId,
                files,
                knowledgeBaseIds,
                isHidden,
                projectContext,
                ...(widgetAction ? { widgetAction } : {}),
              });
            } else {
              logger.warn(
                'No sender function set. This is only available for chat sessions when loading an existing chat. It will not be sent for new chat creations.',
              );
            }
          },
          setMessages: (setter) => set((state) => ({ messages: setter(state.messages) })),

          // AI status - synced from SDK in useAiChat (pure SDK state, never set manually)
          setAiStatus: (aiStatus) =>
            set((state) => {
              // When SDK confirms submission, clear the UI 'submitting' state
              if (aiStatus === 'submitted' && state.aiStatus !== 'submitted') {
                return {
                  aiStatus,
                  busyAgentId: state.currentAgentId,
                  uiStatus: state.uiStatus === 'submitting' ? null : state.uiStatus,
                };
              }
              // When SDK starts streaming, also clear 'submitting' if still set
              if (aiStatus === 'streaming') {
                return {
                  aiStatus,
                  uiStatus: state.uiStatus === 'submitting' ? null : state.uiStatus,
                };
              }
              // When the request completes or errors, clear the busy agent
              if (aiStatus === 'ready' || aiStatus === 'error' || aiStatus === null) {
                return { aiStatus, busyAgentId: null };
              }
              return { aiStatus };
            }),

          // UI status - for immediate feedback before SDK responds
          setUiStatus: (uiStatus) => set({ uiStatus }),

          // Stream event actions
          setStreamEvent: (eventId: string, event: StreamMessageEvents) =>
            set((state) => {
              const newEvents = new Map(state.events);
              newEvents.set(eventId, event);
              return { events: newEvents };
            }),
          clearStreamEvents: () => set({ events: new Map() }),

          fetchSuggestions: async () => {
            const state = get();

            if (!state.currentAgentId) return;
            if (areSuggestionsHidden()) {
              suggestionsBehind = true;
              return;
            }
            suggestionsBehind = false;
            // A built agent says for itself whether it offers suggestions; the app's config, which the
            // callers checked, knows only the platform's own agents. Its card is not compared by id:
            // a chat opened on a legacy id is served the card under the definition id.
            if (loadingAgentId !== state.currentAgentId && state.agent?.suggestions === false) {
              return;
            }

            if (state.isSuggestionsLoading) {
              // Clear stuck loading state and retry
              set({ isSuggestionsLoading: false });
              // Brief delay then retry
              setTimeout(() => {
                void get().fetchSuggestions();
              }, 200);
              return;
            }

            cancelSuggestionsFetch();
            const controller = new AbortController();
            suggestionsAbortController = controller;
            try {
              set({ isSuggestionsLoading: true });

              const response = await getChatSuggestions({
                agentId: state.currentAgentId,
                chatId: state.chatId ?? undefined,
                count: 3,
                signal: controller.signal,
              });
              set({ suggestions: response.suggestions, isSuggestionsLoading: false });
            } catch (error) {
              // Read off the signal: getChatSuggestions rewraps every error, dropping the abort's name.
              if (controller.signal.aborted) {
                set({ isSuggestionsLoading: false });
                return;
              }

              logger.error('Failed to fetch suggestions:', error);
              suggestionsBehind = true;
              // Don't clear suggestions on error - keep existing ones
              // This prevents losing context-aware suggestions due to temporary API failures
              set({
                isSuggestionsLoading: false,
              });
            }
          },

          catchUpSuggestions: async () => {
            const { aiStatus } = get();
            // A turn in progress fetches when it ends, against the conversation this one would miss.
            if (aiStatus === 'submitted' || aiStatus === 'streaming') return;
            if (suggestionsBehind) await get().fetchSuggestions();
          },

          setIsConnected: (connected: boolean) => set({ isConnected: connected }),
          setError: (error: string | null) => set({ error }),
          setLoading: (loading: boolean) => set({ uiStatus: loading ? 'loading' : null }),

          // Reset
          reset: () => {
            cancelSuggestionsFetch();
            suggestionsBehind = false;
            set(initialState);
          },

          reloadCurrentAgent,

          setAgentById: async (agentId, isHandoff = false, options = {}) => {
            // Callers forward ids from the server (chat history, stream events) that can be absent.
            // Checked before the optimistic write below, which subscribers render before any revert.
            // No id means nothing to switch to, so the current agent stays.
            if (!agentId) {
              logger.warn('[ChatStore] setAgentById called without an agent id; ignoring');
              return;
            }
            const {
              agent: prevAgent,
              currentAgentId: prevAgentId,
              isAgentMissing: prevMissing,
            } = get();

            cancelSuggestionsFetch();

            // Immediate optimistic update for smooth UX
            const isSameAgent = prevAgentId === agentId;
            const shouldShowSwitching = !isHandoff && !isSameAgent && prevAgent !== null;

            set((prev) => ({
              error: null,
              uiStatus: shouldShowSwitching ? 'switching-agent' : prev.uiStatus,
              currentAgentId: agentId, // Optimistic update
              // Reset chat input state, only keep files
              chatInput: {
                ...initialState.chatInput,
                files: prev.chatInput.files,
              },
              // Clear suggestions from previous agent to avoid showing stale suggestions
              suggestions: [],
              isSuggestionsLoading: false,
            }));

            try {
              await loadAgent(agentId);

              if (options.withInitialWidget ?? true) {
                applyInitialWidget(agentId);
              }

              if (isHandoff || !shouldShowSwitching) {
                return;
              }

              // Keep switching state for a brief moment to show transition
              return await new Promise<void>((resolve) => {
                setTimeout(() => {
                  set({ uiStatus: null });
                  resolve();
                }, 150); // Shorter delay for smoother transitions
              });
            } catch (error) {
              // Revert optimistic update on error
              set({
                currentAgentId: prevAgentId,
                agent: prevAgent,
                isAgentMissing: prevMissing,
                uiStatus: isHandoff ? get().uiStatus : null,
                // eslint-disable-next-line i18next/no-literal-string -- held in `store.error`, which no consumer renders
                error: error instanceof Error ? error.message : 'Failed to fetch agent',
              });
            }
          },
          setAgentState: (setter) =>
            set((state) => ({
              currentAgentState:
                typeof setter === 'function' ? setter(state.currentAgentState) : setter,
            })),

          // Chat initialization
          initializeChat: async (agentId?: string) => {
            try {
              // Use provided agentId, or fall back to currentAgentId from store
              const targetAgentId =
                typeof agentId === 'string' && agentId.trim() !== ''
                  ? agentId
                  : get().currentAgentId;
              const { id: chatId } = await createChatId(targetAgentId);
              // The session list queries inherit the global 5-minute staleTime,
              // so without invalidation a freshly created session is missing
              // from the sessions page / recent sessions until the cache expires.
              void queryClient.invalidateQueries({ queryKey: [SESSIONS_QUERY_KEY_ROOT] });
              void queryClient.invalidateQueries({ queryKey: [RECENT_SESSIONS_QUERY_KEY_ROOT] });
              return chatId;
            } catch (error) {
              logger.error('[ChatStore] Failed to initialize chat:', error);
              set({
                // eslint-disable-next-line i18next/no-literal-string -- held in `store.error`, which no consumer renders
                error: error instanceof Error ? error.message : 'Failed to initialize chat',
              });
              throw error;
            }
          },
        };
      },
      { name, enabled: inDevtools },
    ),
  );

export type ChatStoreHook = ReturnType<typeof createChatStoreHook>;

/** The app's chat store, which every chat reads unless it sits inside a `ChatStoreScope`. */
const appChatStore = createChatStoreHook('chat-store', true);

// The card is worded in the language of the request that fetched it. The app store lives as long
// as the page, so this listener is never removed; a scoped store subscribes for its own lifetime.
i18n.on('languageChanged', () => void appChatStore.getState().reloadCurrentAgent());

/**
 * A chat store of its own, for a conversation that runs beside the app's chat on one page (the
 * agent builder's Copilot beside its test chat), so the two never write into each other.
 */
// Kept out of the devtools: each conversation would register a connection that is never closed.
export const createChatStore = (): ChatStoreHook => createChatStoreHook('chat-store:scoped', false);

export const ChatStoreContext = createContext<ChatStoreHook | null>(null);

/** The chat store of the conversation this component sits in: its scope's, else the app's. */
export function useChatStoreApi(): ChatStoreHook {
  return useContext(ChatStoreContext) ?? appChatStore;
}

type ChatStoreState = ChatState & ChatActions;
const wholeState = (state: ChatStoreState) => state;

function useScopedChatStore<T = ChatStoreState>(selector?: (state: ChatStoreState) => T): T {
  const select = (selector ?? wholeState) as (state: ChatStoreState) => T;
  // The scope's own bound hook: every store is one, so this is the same subscription the app
  // store's hook makes.
  const store = useChatStoreApi();
  return store(select);
}

/**
 * Reads the chat store of the conversation the component sits in. Its static `getState`,
 * `setState` and `subscribe` are the app store's, since code outside React has no scope to read:
 * code that may run inside a scope takes the store from `useChatStoreApi` instead.
 */
export const useChatStore = Object.assign(useScopedChatStore, {
  getState: appChatStore.getState,
  setState: appChatStore.setState,
  subscribe: appChatStore.subscribe,
  getInitialState: appChatStore.getInitialState,
}) as unknown as ChatStoreHook;

// Set Sentry tag when agent changes for session replay filtering
appChatStore.subscribe((state, prevState) => {
  if (state.currentAgentId && state.currentAgentId !== prevState.currentAgentId) {
    Sentry.setTag('current-agent', state.currentAgentId);
  }
});

// Selector hooks for accessing specific state slices
export const useMessages = () => useChatStore((state) => state.messages);
export const useSuggestions = () => useChatStore((state) => state.suggestions);
export const useIsSuggestionsLoading = () => useChatStore((state) => state.isSuggestionsLoading);
export const useIsConnected = () => useChatStore((state) => state.isConnected);
export const useCurrentAgent = () => useChatStore((state) => state.agent);
export const useCurrentAgentId = () => useChatStore((state) => state.currentAgentId);
/** Whether the current agent can take messages no longer: deleted, or its card failed to load. */
export const useIsCurrentAgentUnavailable = () => useChatStore(isAgentUnavailable);
/** Whether the current agent's card could not be loaded, so `useCurrentAgent` stays `null` for good. */
export const useIsCurrentAgentMissing = () => useChatStore((state) => state.isAgentMissing);
export const useChatId = () => useChatStore((state) => state.chatId);
export const useChatStoreInput = () =>
  useChatStore((state) => ({
    chatInput: state.chatInput,
    setValue: state.setInputValue,
    setFiles: state.setInputFiles,
    setOptions: state.setInputOptions,
  }));
export const useInputOptions = () => useChatStore((state) => state.chatInput.options);
// Narrow selectors so chips on past messages don't re-render on unrelated
// composer changes (text input, file uploads): they subscribe only to the
// stable setter and to the project-context selection.
export const useSetInputOptions = () => useChatStore((state) => state.setInputOptions);
export const useProjectContextSelection = () =>
  useChatStore(
    (state) => state.chatInput.options?.projectContext as ProjectContextSelection | undefined,
  );
export const useChatError = () => useChatStore((state) => state.error);

// UI status selectors
export const useChatLoading = () => useChatStore((state) => state.uiStatus === 'loading');
export const useIsSwitching = () => useChatStore((state) => state.uiStatus === 'switching-agent');

// AI status selectors
// useIsAiBusy includes uiStatus === 'submitting' to cover the gap before SDK responds
export const useIsAiBusy = () =>
  useChatStore(
    (state) =>
      state.uiStatus === 'submitting' ||
      state.aiStatus === 'submitted' ||
      state.aiStatus === 'streaming',
  );
export const useAiStatus = () => useChatStore((state) => state.aiStatus);

// Boolean selector for current agent busy state
// Includes 'submitting' since user just clicked on this agent
export const useIsCurrentAgentBusy = () =>
  useChatStore(
    (state) =>
      state.uiStatus === 'submitting' ||
      ((state.aiStatus === 'submitted' || state.aiStatus === 'streaming') &&
        state.busyAgentId === state.currentAgentId),
  );

export const useChatActions = () =>
  useChatStore((state) => ({
    sendMessage: state.sendMessage,
    setMessages: state.setMessages,
    setSender: state.setSender,
    setAiStatus: state.setAiStatus,
    setUiStatus: state.setUiStatus,
    fetchSuggestions: state.fetchSuggestions,
    catchUpSuggestions: state.catchUpSuggestions,
    setIsConnected: state.setIsConnected,
    setAgentById: state.setAgentById,
    reloadCurrentAgent: state.reloadCurrentAgent,
    setError: state.setError,
    setLoading: state.setLoading,
    reset: state.reset,
    initializeChat: state.initializeChat,
  }));
export const useCurrentAgentState = () => useChatStore((state) => state.currentAgentState);
export const useUpdateAgentState = () => useChatStore((state) => state.setAgentState);
